import React from "react";
import axios from "axios";
import { ADMIN_TOP_ACHIEVERS } from "../../../routes/routes";
import { OvalLoader } from "../../../components/common/Loader";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import Excel from "exceljs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { styleExcelHeaderRow } from "../../../utils/excelStyles";
import moment from "moment";

// Custom Medal SVG Component
const MedalSVG = ({ color = "#CF2030" }) => (
  <svg
    className="top-achiever-medal-svg"
    width="54"
    height="72"
    viewBox="0 0 54 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: "absolute",
      top: "0px",
      left: "50%",
      transform: "translateX(-50%)",
      zIndex: 10,
      transition: "transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
    }}
  >
    {/* Ribbon */}
    <path d="M17 0H37V32L27 24L17 32V0Z" fill="#7d121c" />
    <path d="M17 0H27V24L17 32V0Z" fill="#CF2030" />
    {/* Medal Base (Outer glow) */}
    <circle cx="27" cy="45" r="21" fill="#fef08a" />
    {/* Medal Inner */}
    <circle cx="27" cy="45" r="16" fill="#d97706" />
    {/* Star */}
    <path
      d="M27 35L29.245 40.8541H35.0764L30.3657 44.5918L32.1631 50.4459L27 46.9582L21.8369 50.4459L23.6343 44.5918L18.9236 40.8541H24.755L27 35Z"
      fill="white"
    />
  </svg>
);

const PERIOD_TABS = [
  { id: "week", label: "This Week" },
  { id: "month", label: "Last Month" },
  { id: "overall", label: "Overall" },
];

const TopAchievers = ({ data, ovalLoading, hasPermission, filter }) => {
  const [activeTab, setActiveTab] = React.useState("week");
  const [fetchingPeriod, setFetchingPeriod] = React.useState(false);
  const [cachedPeriods, setCachedPeriods] = React.useState({});
  const [excelLoading, setExcelLoading] = React.useState(false);

  // Clear cache and refetch active tab when filter changes
  React.useEffect(() => {
    setCachedPeriods({});
    if (activeTab !== "week") {
      const fetchCurrentTab = async () => {
        try {
          setFetchingPeriod(true);
          const response = await axios.get(ADMIN_TOP_ACHIEVERS, {
            params: {
              period: activeTab,
              country_id: filter?.country_id || "",
              state_id: filter?.state_id || "",
              city_id: filter?.city_id || "",
              region_id: filter?.region_id || "",
              chapter_id: filter?.chapter_id || "",
            },
          });
          if (response.status === 200 && response.data.success) {
            const resData = response.data.data;
            setCachedPeriods({
              [activeTab]: {
                list: resData.top3AchieversList,
                startDate: resData.week_start_date,
                endDate: resData.week_end_date,
                meetingDate: resData.meeting_date,
                monthName: resData.month_name,
              },
            });
          }
        } catch (err) {
          console.error("Filter change top achievers fetch error:", err);
        } finally {
          setFetchingPeriod(false);
        }
      };
      fetchCurrentTab();
    }
  }, [
    filter?.country_id,
    filter?.state_id,
    filter?.city_id,
    filter?.region_id,
    filter?.chapter_id,
  ]);

  const handleTabChange = async (tabId) => {
    if (tabId === activeTab) return;
    setActiveTab(tabId);

    if (tabId === "week" && (cachedPeriods["week"] || data?.thisWeekTop3AchieversList)) {
      return;
    }
    if (cachedPeriods[tabId]) {
      return;
    }

    try {
      setFetchingPeriod(true);
      const response = await axios.get(ADMIN_TOP_ACHIEVERS, {
        params: {
          period: tabId,
          country_id: filter?.country_id || "",
          state_id: filter?.state_id || "",
          city_id: filter?.city_id || "",
          region_id: filter?.region_id || "",
          chapter_id: filter?.chapter_id || "",
        },
      });

      if (response.status === 200 && response.data.success) {
        const resData = response.data.data;
        setCachedPeriods((prev) => ({
          ...prev,
          [tabId]: {
            list: resData.top3AchieversList,
            startDate: resData.week_start_date,
            endDate: resData.week_end_date,
            meetingDate: resData.meeting_date,
            monthName: resData.month_name,
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch top achievers for period:", tabId, error);
      toast.error(error?.response?.data?.message || "Failed to load top achievers");
    } finally {
      setFetchingPeriod(false);
    }
  };

  const currentPeriodData = React.useMemo(() => {
    if (activeTab === "week") {
      if (cachedPeriods["week"]) return cachedPeriods["week"];
      return {
        list: data?.thisWeekTop3AchieversList,
        startDate: data?.week_start_date,
        endDate: data?.week_end_date,
        meetingDate: data?.nextMeetingDate,
      };
    }
    return (
      cachedPeriods[activeTab] || {
        list: null,
        startDate: "",
        endDate: "",
      }
    );
  }, [activeTab, cachedPeriods, data]);

  const currentList = currentPeriodData.list || {};

  const handleDownloadExcel = async () => {
    try {
      setExcelLoading(true);
      const workbook = new Excel.Workbook();
      const workSheetName = "Top Achievers";
      const worksheet = workbook.addWorksheet(workSheetName);

      // Define columns
      worksheet.columns = [
        { header: "S.No", key: "sno", width: 10 },
        { header: "Category", key: "category", width: 20 },
        { header: "Name", key: "name", width: 30 },
        { header: "Company", key: "company", width: 30 },
        { header: "Value", key: "value", width: 15 },
        { header: "Country", key: "country", width: 20 },
        { header: "State", key: "state", width: 20 },
        { header: "City", key: "city", width: 20 },
        { header: "Region", key: "region", width: 20 },
        { header: "Circle", key: "chapter", width: 20 },
      ];

      // Add Header Style
      styleExcelHeaderRow(worksheet.getRow(1), worksheet.columns.length);

      let sno = 1;

      activeCategories.forEach((cat) => {
        if (cat.list && cat.list.length > 0) {
          cat.list.forEach((user) => {
            worksheet.addRow({
              sno: sno++,
              category: cat.title,
              name: user[cat.tooltipNameKey] || user[cat.nameKey] || "",
              company: user.user_company_name || "",
              value: user[cat.valKey] || 0,
              country: user.country_name || "",
              state: user.state_name || "",
              city: user.city_name || "",
              region: user.region_name || "",
              chapter: user.chapter_name || "",
            });
          });
        }
      });

      worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: "thin" },
              left: { style: "thin" },
              bottom: { style: "thin" },
              right: { style: "thin" },
            };
          });
        }
      });

      const buf = await workbook.xlsx.writeBuffer();
      const periodLabel =
        activeTab === "week"
          ? "This_Week"
          : activeTab === "month"
          ? "Last_Month"
          : "Overall";
      const startSlug = currentPeriodData.startDate
        ? String(currentPeriodData.startDate)
            .replace(/[\/:]/g, "-")
            .replace(/\s+/g, "_")
        : "";
      const endSlug = currentPeriodData.endDate
        ? String(currentPeriodData.endDate)
            .replace(/[\/:]/g, "-")
            .replace(/\s+/g, "_")
        : "";
      const dateRangeSlug =
        startSlug && endSlug ? `${startSlug}_to_${endSlug}` : "Overall";

      saveAs(new Blob([buf]), `Top_Achievers_${periodLabel}_${dateRangeSlug}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error.message);
    } finally {
      setExcelLoading(false);
    }
  };

  if (
    !hasPermission("dashboard-tyns-data-view") &&
    !hasPermission("dashboard-121s-data-view") &&
    !hasPermission("dashboard-rfls-data-view") &&
    !hasPermission("dashboard-visitors-data-view")
  ) {
    return null;
  }

  // Define categories array to dynamically map
  const categories = [
    {
      id: "tyfcbs",
      crownType: "tyns",
      permission: "dashboard-tyns-data-view",
      title: "Biz Given",
      list: currentList?.tyfcbsList || [],
      overallTitle: "OVERALL",
      overallValue: currentList?.thisWeekOverallTyfcb,
      valKey: "tyfcb_amount",
      formatVal: (v) => `${v}`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "121s",
      crownType: "121s",
      permission: "dashboard-121s-data-view",
      title: "Personal Meet",
      list: currentList?.onetooneList || [],
      overallTitle: "OVERALL",
      overallValue: currentList?.thisWeek121s,
      valKey: "one2one_count",
      formatVal: (v) => `${v} Meetings`,
      imgKey: "thumb_from_profile_image",
      nameKey: "from_user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "rfls",
      crownType: "rfls",
      permission: "dashboard-rfls-data-view",
      title: "Professional Connect",
      list: currentList?.referralsList || [],
      overallTitle: "OVERALL",
      overallValue: currentList?.thisWeekRfls,
      valKey: "referrals_count",
      formatVal: (v) => `${v} Professional Connects`,
      imgKey: "thumb_from_profile_image",
      nameKey: "from_user_name",
      tooltipNameKey: "from_user_name",
    },
    {
      id: "inductions",
      crownType: "inductions",
      permission: "dashboard-visitors-data-view",
      title: "Inductions",
      list: currentList?.inductionsVisitorsList || [],
      overallTitle: "OVERALL",
      overallValue: currentList?.thisWeekInductions,
      valKey: "visitors_count",
      formatVal: (v) => `${v} Inductions`,
      imgKey: "thumb_user_profile_image",
      nameKey: "user_name",
      tooltipNameKey: "from_user_name",
    },
  ];

  // Filter out categories user doesn't have permission for
  const activeCategories = categories.filter((cat) =>
    hasPermission(cat.permission),
  );

  if (activeCategories.length === 0) {
    return null;
  }

  const titleText = "Top Achievers";

  const subtitleText =
    currentPeriodData.startDate && currentPeriodData.endDate
      ? `${currentPeriodData.startDate} - ${currentPeriodData.endDate}`
      : "";

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card mb-3 custom-card border-0 shadow-sm rounded-4 overflow-hidden">
          <div
            className="card-header border-0 d-flex justify-content-between align-items-center"
            style={{
              background: "linear-gradient(to right, #CF2030, #B71C2A)",
              padding: "24px 32px",
              position: "relative",
              overflow: "hidden",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            {/* Glossy wave reflection simulating the image */}
            <div
              style={{
                position: "absolute",
                top: "-50%",
                left: "-10%",
                width: "120%",
                height: "130%",
                background:
                  "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 70%)",
                transform: "rotate(-4deg)",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "45%",
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 100%)",
                pointerEvents: "none",
              }}
            />

            <div style={{ position: "relative", zIndex: 1 }}>
              <h2
                style={{
                  color: "#fff",
                  margin: 0,
                  fontWeight: 700,
                  fontSize: "1.4rem",
                  letterSpacing: "-0.01em",
                }}
              >
                {titleText}
              </h2>
              {subtitleText && (
                <p
                  style={{
                    color: "rgba(255, 255, 255, 0.9)",
                    margin: "4px 0 0 0",
                    fontSize: "0.90rem",
                  }}
                >
                  {subtitleText}
                </p>
              )}
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                position: "relative",
                zIndex: 1,
                flexWrap: "wrap",
              }}
            >
              {/* Segmented Pill Toggle: This Week | This Month | Overall */}
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  backgroundColor: "rgba(0, 0, 0, 0.22)",
                  padding: "4px",
                  borderRadius: "9999px",
                  boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.25)",
                }}
              >
                {PERIOD_TABS.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleTabChange(tab.id)}
                      disabled={fetchingPeriod}
                      style={{
                        border: "none",
                        outline: "none",
                        cursor: fetchingPeriod ? "not-allowed" : "pointer",
                        padding: "7px 20px",
                        borderRadius: "9999px",
                        fontSize: "0.88rem",
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? "#0f172a" : "rgba(255, 255, 255, 0.92)",
                        backgroundColor: isActive ? "#ffffff" : "transparent",
                        boxShadow: isActive
                          ? "0 2px 8px rgba(0, 0, 0, 0.22)"
                          : "none",
                        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                        userSelect: "none",
                        whiteSpace: "nowrap",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor =
                            "rgba(255, 255, 255, 0.12)";
                          e.currentTarget.style.color = "#ffffff";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color =
                            "rgba(255, 255, 255, 0.92)";
                        }
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Excel Download Button */}
              <button
                onClick={() => {
                  if (!excelLoading) handleDownloadExcel();
                }}
                style={{
                  background: "rgba(255, 255, 255, 0.12)",
                  border: "none",
                  color: "#e2e8f0",
                  padding: "8px 20px",
                  borderRadius: "8px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  cursor: excelLoading ? "not-allowed" : "pointer",
                  opacity: excelLoading ? 0.7 : 1,
                  transition: "background 0.2s",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                onMouseOver={(e) => {
                  if (!excelLoading)
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.2)";
                }}
                onMouseOut={(e) => {
                  if (!excelLoading)
                    e.currentTarget.style.background =
                      "rgba(255, 255, 255, 0.12)";
                }}
              >
                {excelLoading ? (
                  <>
                    Downloading... <i className="bx bx-loader-alt bx-spin"></i>
                  </>
                ) : (
                  <>
                    Download{" "}
                    <i
                      className="bx bx-right-arrow-alt"
                      style={{ fontSize: "1.2rem", marginTop: "1px" }}
                    ></i>
                  </>
                )}
              </button>
            </div>
          </div>

          <div
            className="card-body bg-white position-relative top-achievers-swiper-wrapper"
            style={{ padding: "16px", minHeight: "260px" }}
          >
            {fetchingPeriod && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.75)",
                  backdropFilter: "blur(2px)",
                  zIndex: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                <OvalLoader />
              </div>
            )}
            <Swiper
              modules={[Navigation]}
              navigation
              spaceBetween={24}
              slidesPerView={4}
              style={{ padding: "0 4px" }}
            >
              {activeCategories.map((cat) => (
                <SwiperSlide
                  key={cat.id}
                  style={{
                    height: "auto",
                    display: "flex",
                  }}
                >
                  <div
                    className="top-achiever-card"
                    style={{
                      background: "#F8F4F5",
                      borderRadius: "16px",
                      padding: "80px 16px 24px",
                      width: "100%",
                      flex: "1 1 0",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <MedalSVG />

                    <h4
                      style={{
                        color: "#CF2030",
                        fontWeight: 800,
                        fontSize: "1.15rem",
                        textAlign: "center",
                        marginTop: "12px",
                        marginBottom: "24px",
                        lineHeight: "1.3",
                        textTransform: "capitalize",
                      }}
                    >
                      {cat.title}
                    </h4>

                    <div
                      style={{
                        width: "100%",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {ovalLoading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            flex: 1,
                            alignItems: "center",
                          }}
                        >
                          <OvalLoader color="#CF2030" />
                        </div>
                      ) : cat.list?.length > 0 ? (
                        cat.list.map((user, i) => (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              marginBottom: "20px",
                              width: "100%",
                            }}
                          >
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip className="top-achiever-tooltip custom-green-tooltip">
                                  {user.sno || i + 1}.{" "}
                                  {user[cat.tooltipNameKey] ||
                                    user[cat.nameKey]}{" "}
                                  ({user.user_company_name || ""}) -{" "}
                                  {user.country_name || ""} -{" "}
                                  {user.state_name || ""} -{" "}
                                  {user.city_name || ""} -{" "}
                                  {user.region_name || ""} -{" "}
                                  {user.chapter_name || ""}
                                </Tooltip>
                              }
                            >
                              <img
                                src={
                                  user[cat.imgKey] ||
                                  user.thumb_profile_image ||
                                  user.thumb_user_profile_image ||
                                  user.thumb_from_profile_image ||
                                  user.profile_image ||
                                  user.user_profile_image ||
                                  user.from_profile_image
                                }
                                alt="profile"
                                style={{
                                  width: "42px",
                                  height: "42px",
                                  borderRadius: "50%",
                                  objectFit: "cover",
                                  flexShrink: 0,
                                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                  cursor: "pointer",
                                }}
                              />
                            </OverlayTrigger>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                              }}
                            >
                              <span
                                style={{
                                  color: "#566085",
                                  fontWeight: 700,
                                  fontSize: "0.95rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {user[cat.nameKey]}
                              </span>
                              {/* Client: crown master UI paused for now
                              <span
                                style={{
                                  color: "#566085",
                                  fontWeight: 700,
                                  fontSize: "0.95rem",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  maxWidth: "100%",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                }}
                              >
                                <span
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                >
                                  {user[cat.nameKey]}
                                </span>
                                {i === 0 &&
                                  data?.crownMastersByType?.[cat.crownType]
                                    ?.image && (
                                    <img
                                      src={
                                        data.crownMastersByType[cat.crownType]
                                          .image
                                      }
                                      alt={
                                        data.crownMastersByType[cat.crownType]
                                          .title || "Crown"
                                      }
                                      title={
                                        data.crownMastersByType[cat.crownType]
                                          .title || ""
                                      }
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        objectFit: "contain",
                                        flexShrink: 0,
                                      }}
                                    />
                                  )}
                              </span>
                              {i === 0 &&
                                data?.crownMastersByType?.[cat.crownType]
                                  ?.title && (
                                  <span
                                    style={{
                                      color: "#8e7c13",
                                      fontSize: "0.7rem",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      marginTop: "2px",
                                    }}
                                  >
                                    {
                                      data.crownMastersByType[cat.crownType]
                                        .title
                                    }
                                  </span>
                                )}
                              */}
                              <span
                                style={{
                                  color: "#333",
                                  fontSize: "0.8rem",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {cat.formatVal(user[cat.valKey] || 0)}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "20px 0",
                            textAlign: "center",
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "50%",
                              backgroundColor: "rgba(0,0,0,0.04)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginBottom: "12px",
                            }}
                          >
                            <i
                              className="bx bx-award"
                              style={{ fontSize: "28px", color: "#94a3b8" }}
                            ></i>
                          </div>
                          <span
                            style={{
                              color: "#94a3b8",
                              fontSize: "0.9rem",
                              fontWeight: 500,
                            }}
                          >
                            No achievers yet
                          </span>
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: "16px",
                        paddingTop: "16px",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "0.95rem",
                          color: "#000",
                          fontWeight: 700,
                        }}
                      >
                        Overall: {cat.overallValue || 0}
                      </span>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopAchievers;
